/**
 * Deeplink Manager
 *
 * Unified deeplink handling for both native and web platforms.
 * This module provides:
 * 1. Deeplink initialization (resolving initial URL, setting up listeners)
 * 2. Post-login route storage (for authenticated deeplinks)
 * 3. Biometric auto-login prevention (for public deeplinks like password reset)
 * 4. Analytics and observability
 *
 * Architecture:
 * - InitializationProvider Phase 4 calls initializeDeeplinkHandling()
 * - (authenticated)/_layout.tsx saves path before redirect if not logged in
 * - (authenticated)/_layout.tsx navigates to saved path after login
 */

import { getInitialURL } from 'expo-linking';
import { Href, router } from 'expo-router';
import { Linking, Platform } from 'react-native';
import { logEvent } from 'services/analytics/segment/segment';
import {
  LegacyDeeplinks,
  NotAuthenticatedDeeplinks,
  NotAuthenticatedPartialDeeplinks,
} from 'services/deeplinks/constants';
import { getInitialPushDeeplink } from 'services/deeplinks/helpers/getInitialPushDeeplink';
import { normalizeDeeplinkUrl } from 'services/deeplinks/helpers/normalizeDeeplinkUrl';
import {
  LegacyDeeplinksRule,
  PersistedDeeplink,
} from 'services/deeplinks/types';
import { isWeb } from 'shared/constants/platform';
import { STORAGE_KEYS } from 'shared/constants/storage';
import { getItem, removeItem, setItem } from 'shared/utils/asyncStorage';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

/**
 * Look up a legacy route mapping for a given path
 * Returns the new cruise path if a mapping exists
 */
function getDeeplinkRule(path: string): LegacyDeeplinksRule | undefined {
  return LegacyDeeplinks[path];
}

/**
 * Check if a path is a public route that doesn't require authentication
 * Handles both exact matches and partial matches (for dynamic segments)
 */
function isNotAuthenticatedDeeplink(shortPath: string): boolean {
  // handling deeplinks with dynamic tokens (eg: /challenge/magic-link/[token])
  for (const deeplinkPartialPath of NotAuthenticatedPartialDeeplinks) {
    if (shortPath.includes(deeplinkPartialPath)) {
      return true;
    }
  }

  // handling deeplinks without dynamic tokens (eg: '/login/verify-sms')
  return NotAuthenticatedDeeplinks.includes(shortPath);
}

/**
 * Check if a path is non-navigating (should not change app state)
 * e.g., /open-app just opens the app without changing the current route
 * e.g., /oauth returns from OAuth App 2 App flow without navigating
 */
function isNonNavigating(shortPath: string): boolean {
  return shortPath === '/open-app' || shortPath === '/oauth';
}

// Module-level state for runtime flags only (post-login routes stored in AsyncStorage)
interface DeeplinkState {
  /** Prevent biometric auto-login (for public deeplinks like password reset) */
  preventBiometricAutoLogin: boolean;
  /** Whether user is currently logged in (set by AuthProvider) */
  isLoggedIn: boolean;
  /** Web-only: Initial URL captured at app start, processed once when login state is known */
  webInitialPath: string | null;
  /** Web-only: Whether the initial web deeplink has been processed */
  webDeeplinkProcessed: boolean;
}

let state: DeeplinkState = {
  preventBiometricAutoLogin: false,
  isLoggedIn: false,
  webInitialPath: null,
  webDeeplinkProcessed: false,
};

/**
 * Set login state - called by AuthProvider when auth state changes.
 * This allows deeplink processing to make auth-aware decisions.
 */
export function setIsLoggedIn(value: boolean): void {
  state.isLoggedIn = value;
}

/**
 * Process the captured web deeplink once login state is known.
 * Called by AuthProvider when loginEvaluated becomes true.
 * This runs exactly once per app load - subsequent calls are no-ops.
 */
export async function processWebDeeplink(): Promise<void> {
  // Only run on web, and only once
  if (!isWeb || state.webDeeplinkProcessed) {
    return;
  }

  state.webDeeplinkProcessed = true;

  const pathname = state.webInitialPath;
  state.webInitialPath = null; // Clear after reading

  if (!pathname) {
    return;
  }

  const shortPath = pathname.split('?')[0];

  logBreadcrumb({
    level: 'info',
    message: 'Processing web deeplink',
    data: { pathname, shortPath, isLoggedIn: state.isLoggedIn },
  });

  if (state.isLoggedIn) {
    // User is already logged in - Expo Router will handle the URL directly
    // No need to save or navigate, the URL is already in the address bar
    logBreadcrumb({
      level: 'info',
      message: 'Web deeplink - user logged in, Expo Router handles navigation',
      data: { pathname },
    });
  } else if (!isNotAuthenticatedDeeplink(shortPath)) {
    // User is not logged in - save for post-login redirect
    logBreadcrumb({
      level: 'info',
      message: 'Web deeplink - user not logged in, saving for post-login',
      data: { pathname, shortPath },
    });

    await savePendingRoute(pathname);
  }
}

/**
 * Initialize deeplink handling
 * Called from InitializationProvider Phase 4
 *
 * This function:
 * 1. Gets the initial URL that launched the app
 * 2. Resolves ABLinks/OneLinks if needed
 * 3. Logs analytics
 * 4. Navigates to the deeplink route (or saves for post-login)
 * 5. Sets up listener for future deeplinks
 */
export async function initializeDeeplinkHandling(): Promise<void> {
  logBreadcrumb({
    level: 'info',
    message: 'Initializing deeplink handling',
    data: { platform: Platform.OS },
  });

  try {
    // Web: Capture the initial URL for deferred processing
    // We can't process immediately because we don't know login state yet.
    // processWebDeeplink() will be called by AuthProvider when loginEvaluated becomes true.
    if (isWeb) {
      const pathname = window.location.pathname;
      const search = window.location.search;
      const fullPath = pathname + search;

      // Don't capture root, login, signup, or onboarding paths
      const shouldSkip =
        !pathname ||
        pathname === '/' ||
        pathname.startsWith('/login') ||
        pathname.startsWith('/signup') ||
        pathname.startsWith('/onboarding');

      if (!shouldSkip) {
        state.webInitialPath = fullPath;

        logBreadcrumb({
          level: 'info',
          message:
            'Web platform - captured initial URL for deferred processing',
          data: { fullPath },
        });
      } else {
        logBreadcrumb({
          level: 'info',
          message: 'Web platform - no deeplink to capture',
          data: { pathname },
        });
      }
      return;
    }

    // Native: Handle URL scheme and push notification deeplinks
    // Get and process the initial URL first (cold start deeplink from URL scheme)
    // This handles true deep links where notification tap triggers URL scheme
    const initialUrl = await getInitialURL();
    if (initialUrl) {
      await processDeeplink(initialUrl, true);
      // URL scheme handled the deeplink - don't check notification payload
      // (prevents double processing if notification tap triggered URL scheme)
    } else {
      // No URL scheme deeplink - check notification payload for deeplink_cruise
      // This handles notifications with deeplink_cruise in data payload
      const pushDeeplink = await getInitialPushDeeplink();
      if (pushDeeplink) {
        await processDeeplink(pushDeeplink, true);
      }
    }

    // Set up listener for runtime deeplinks (app already running)
    Linking.addEventListener('url', async (event) => {
      await processDeeplink(event.url, false);
    });

    logBreadcrumb({
      level: 'info',
      message: 'Deeplink handling initialized successfully',
    });
  } catch (error) {
    logBreadcrumb({
      level: 'error',
      message: 'Failed to initialize deeplink handling',
      data: { error },
    });
    // Don't throw - deeplink failure shouldn't block app
  }
}

/**
 * Process a deeplink URL
 *
 * @param url The raw URL to process
 * @param isInitial Whether this is the initial URL that launched the app
 */
async function processDeeplink(url: string, isInitial: boolean): Promise<void> {
  logBreadcrumb({
    level: 'info',
    message: 'Processing deeplink',
    data: { url, isInitial, platform: Platform.OS },
  });

  try {
    // Normalize the URL (resolve ABLinks/OneLinks, extract path)
    const normalized = await normalizeDeeplinkUrl(url);

    // Log analytics
    void logEvent('Deep Link Opened', {
      deep_link_url: normalized.resolvedUrl,
      original_url: normalized.originalUrl,
      path: normalized.path,
      is_initial: isInitial,
      platform: Platform.OS,
    });

    // If not an app link, don't process further
    if (!normalized.isAppLink) {
      logBreadcrumb({
        level: 'info',
        message: 'URL is not an app deeplink, opening in-app webview',
        data: { url: normalized.resolvedUrl },
      });

      // On native, open in an in-app webview.
      // Always use the (public) group path so the auth guard never fires —
      // external links (e.g. Legal Agreement) must open without requiring login.
      if (!isWeb) {
        router.navigate({
          pathname: '/(public)/external-deeplink',
          params: { url: normalized.resolvedUrl },
        } as Href);
      }

      return;
    }

    // Handle non-navigating deeplinks (e.g., /open-app)
    if (isNonNavigating(normalized.path)) {
      logBreadcrumb({
        level: 'info',
        message: 'Non-navigating deeplink, no action needed',
        data: { path: normalized.path },
      });
      return;
    }

    // Apply legacy path mappings
    const rule = getDeeplinkRule(normalized.path);
    const targetPath = rule ? rule.cruisePath : normalized.path;

    // Handle public deeplinks (no auth required)
    if (isNotAuthenticatedDeeplink(targetPath)) {
      logBreadcrumb({
        level: 'info',
        message: 'Public deeplink, navigating',
        data: { path: targetPath },
      });

      // Prevent biometric auto-login for certain public routes
      // (e.g., password reset should show manual login)
      state.preventBiometricAutoLogin = true;

      void logEvent('Deep Link Processed', {
        path: targetPath,
        queryParams: normalized.queryParams,
      });

      router.navigate({
        pathname: targetPath,
        params: normalized.queryParams,
      } as Href);
      return;
    }

    // Authenticated deeplink handling based on login state
    if (state.isLoggedIn) {
      // User is logged in - navigate directly
      logBreadcrumb({
        level: 'info',
        message: 'Authenticated deeplink, user logged in, navigating',
        data: { path: targetPath },
      });

      void logEvent('Deep Link Processed', {
        path: targetPath,
        queryParams: normalized.queryParams,
      });

      router.navigate({
        pathname: targetPath,
        params: normalized.queryParams,
      } as Href);
    } else {
      // User is logged out - persist to AsyncStorage for post-login
      logBreadcrumb({
        level: 'info',
        message: 'Authenticated deeplink, user logged out, persisting for auth',
        data: { path: targetPath },
      });

      await savePendingRoute(targetPath, normalized.queryParams);
    }
  } catch (error) {
    logBreadcrumb({
      level: 'error',
      message: 'Error processing deeplink',
      data: { url, error },
    });

    void logEvent('Deep Link Error', {
      url,
      error: error instanceof Error ? error.message : String(error),
      platform: Platform.OS,
    });
  }
}

/**
 * Save a route for post-login/post-sync navigation.
 * Used by:
 * - processDeeplink() on native when user is logged out (deeplink)
 * - (authenticated)/_layout.tsx on web for unauthenticated users (deeplink)
 * - Approved.tsx after signup to navigate to onboarding post-sync (not a deeplink)
 *
 * @param route - The route path to save
 * @param params - Optional route parameters
 * @param options.isDeeplink - Whether this is a deeplink (enables analytics). Default: true
 */
export async function savePendingRoute(
  route: string,
  params: Record<string, string> = {},
  options: { isDeeplink?: boolean } = { isDeeplink: true },
): Promise<void> {
  try {
    const persistedDeeplink: PersistedDeeplink = { route, params };
    await setItem(
      STORAGE_KEYS.PENDING_DEEPLINK,
      JSON.stringify(persistedDeeplink),
    );

    if (options.isDeeplink) {
      void logEvent('Deep Link Persisted For Auth', {
        path: route,
        queryParams: params,
      });

      logBreadcrumb({
        level: 'info',
        message: 'Saved pending deeplink for auth',
        data: { route, params },
      });
    } else {
      logBreadcrumb({
        level: 'info',
        message: 'Saved pending route for post-sync navigation',
        data: { route, params },
      });
    }
  } catch (error) {
    logBreadcrumb({
      level: 'error',
      message: 'Failed to save pending route',
      data: { route, error },
    });
  }
}

/**
 * Clear any pending deeplink from storage
 * Used when user manually navigates to signup to prevent unwanted post-login redirects
 */
export async function clearPendingDeeplink(): Promise<void> {
  try {
    await removeItem(STORAGE_KEYS.PENDING_DEEPLINK);
    logBreadcrumb({
      level: 'info',
      message: 'Pending deeplink cleared',
    });
  } catch (error) {
    logBreadcrumb({
      level: 'error',
      message: 'Error clearing pending deeplink',
      data: { error },
    });
  }
}

/**
 * Get and clear the post-login route from AsyncStorage
 * Called by (authenticated)/_layout.tsx after successful login
 */
export async function getAndClearPostLoginRoute(): Promise<PersistedDeeplink | null> {
  try {
    const stored = await getItem(STORAGE_KEYS.PENDING_DEEPLINK);

    if (!stored) {
      return null;
    }

    // Clear storage after reading
    await clearPendingDeeplink();

    const persisted: PersistedDeeplink = JSON.parse(stored);

    logBreadcrumb({
      level: 'info',
      message: 'Retrieving and clearing post-login route',
      data: { route: persisted.route },
    });

    void logEvent('Deep Link Processed', {
      path: persisted.route,
      queryParams: persisted.params,
    });

    return persisted;
  } catch (error) {
    logBreadcrumb({
      level: 'error',
      message: 'Error reading post-login route from storage',
      data: { error },
    });

    // Clear potentially corrupt storage
    await clearPendingDeeplink();

    return null;
  }
}

/**
 * Set whether biometric auto-login should be prevented
 * Used for public deeplinks like password reset
 */
export function setPreventBiometricAutoLogin(prevent: boolean): void {
  state.preventBiometricAutoLogin = prevent;
}

/**
 * Check if biometric auto-login should be prevented
 */
export function shouldPreventBiometricAutoLogin(): boolean {
  return state.preventBiometricAutoLogin;
}
